import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  MapPin, Zap, Calendar, Plus, Search, Filter,
  Edit, Trash2, Building2, Info, Image as ImageIcon,
  Upload, X, ZoomIn, ZoomOut, RotateCw, Download,
  Eye, Move, Maximize2
} from 'lucide-react';

// ── Design tokens ─────────────────────────────────────────────────
const T = {
  bg:      'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  surf2:   'var(--nd-surface-2)',
  border:  'var(--nd-w08)',
  bord2:   'var(--nd-w10)',
  text:    'var(--nd-text)',
  muted:   'var(--nd-tx35)',
  faint:   'var(--nd-w12)',
  blue:    '#3B82F6',
  teal:    '#22D3A5',
  amber:   '#FBBF24',
  red:     '#F87171',
  purple:  '#A78BFA',
  font:    "'DM Sans', sans-serif",
  mono:    "'DM Mono', monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeIn  { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
  @keyframes modalIn { from{opacity:0;transform:scale(0.97)} to{opacity:1;transform:scale(1)} }

  .sm-row { transition: background 0.15s; }
  .sm-row:hover { background: var(--nd-w03) !important; }

  .sm-input {
    width:100%; padding:10px 12px;
    background:var(--nd-w05);
    border:1px solid var(--nd-w10);
    border-radius:10px; color:var(--nd-text);
    font-size:13px; font-family:'DM Sans',sans-serif;
    outline:none; box-sizing:border-box;
    transition:border-color 0.15s,box-shadow 0.15s,background 0.15s;
  }
  .sm-input:focus {
    background:var(--nd-w08);
    border-color:rgba(96,165,250,0.5);
    box-shadow:0 0 0 3px rgba(96,165,250,0.10);
  }
  .sm-input::placeholder { color:var(--nd-w20); }

  .sm-select {
    width:100%; padding:10px 12px;
    background:var(--nd-w05);
    border:1px solid var(--nd-w10);
    border-radius:10px; color:var(--nd-text);
    font-size:13px; font-family:'DM Sans',sans-serif;
    outline:none; cursor:pointer; box-sizing:border-box;
    -webkit-appearance:none; appearance:none;
    transition:border-color 0.15s;
  }
  .sm-select:focus { border-color:rgba(96,165,250,0.5); box-shadow:0 0 0 3px rgba(96,165,250,0.10); }
  .sm-select option { background:var(--nd-surface-3); color:var(--nd-text); }

  .sm-textarea {
    width:100%; padding:10px 12px;
    background:var(--nd-w05);
    border:1px solid var(--nd-w10);
    border-radius:10px; color:var(--nd-text);
    font-size:13px; font-family:'DM Sans',sans-serif;
    outline:none; resize:vertical; box-sizing:border-box;
    transition:border-color 0.15s;
  }
  .sm-textarea:focus { border-color:rgba(96,165,250,0.5); box-shadow:0 0 0 3px rgba(96,165,250,0.10); }
  .sm-textarea::placeholder { color:var(--nd-w20); }

  .sm-label {
    display:block; font-size:10px; font-weight:700;
    letter-spacing:0.10em; text-transform:uppercase;
    color:var(--nd-tx35); margin-bottom:6px;
  }
  .sm-input-icon { position:relative; }
  .sm-input-icon svg { position:absolute; left:12px; top:50%; transform:translateY(-50%); pointer-events:none; }
  .sm-input-icon .sm-input { padding-left:38px; }

  .sm-btn-primary {
    padding:10px 20px; border:none; border-radius:10px; cursor:pointer;
    font-family:'DM Sans',sans-serif; font-size:13px; font-weight:700;
    background:linear-gradient(135deg,#3B82F6,#22D3A5); color:var(--nd-text-strong);
    display:flex; align-items:center; gap:7px;
    box-shadow:0 4px 14px rgba(59,130,246,0.3);
    transition:opacity 0.15s, transform 0.15s;
  }
  .sm-btn-primary:hover { opacity:0.9; transform:translateY(-1px); }
  .sm-btn-primary:disabled { opacity:0.5; cursor:not-allowed; transform:none; }

  .sm-btn-ghost {
    padding:10px 20px; border:1px solid var(--nd-w12); border-radius:10px; cursor:pointer;
    font-family:'DM Sans',sans-serif; font-size:13px; font-weight:600;
    background:var(--nd-w04); color:var(--nd-tx60);
    display:flex; align-items:center; gap:7px; transition:background 0.15s;
  }
  .sm-btn-ghost:hover { background:var(--nd-w08); color:var(--nd-text); }

  .sm-action-btn {
    width:34px; height:34px; border-radius:9px; border:none; cursor:pointer;
    display:flex; align-items:center; justify-content:center; transition:all 0.15s;
    flex-shrink:0;
  }

  .sm-img-card { transition:transform 0.2s, box-shadow 0.2s; }
  .sm-img-card:hover { transform:translateY(-3px); }

  ::-webkit-scrollbar { width:4px; height:4px; }
  ::-webkit-scrollbar-track { background:transparent; }
  ::-webkit-scrollbar-thumb { background:var(--nd-w10); border-radius:4px; }
`;

// ── Status Badge ───────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = {
    pending:     { bg: 'rgba(251,191,36,0.12)',  color: '#FBBF24', border: 'rgba(251,191,36,0.25)',  label: 'Pending'     },
    in_progress: { bg: 'rgba(59,130,246,0.12)',  color: '#60A5FA', border: 'rgba(59,130,246,0.25)',  label: 'In Progress' },
    completed:   { bg: 'rgba(34,211,165,0.12)',  color: '#22D3A5', border: 'rgba(34,211,165,0.25)',  label: 'Completed'   },
    on_hold:     { bg: 'rgba(248,113,113,0.12)', color: '#F87171', border: 'rgba(248,113,113,0.25)', label: 'On Hold'     },
  }[status] || { bg: 'var(--nd-w06)', color: T.muted, border: T.border, label: status };
  return (
    <span style={{
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      padding: '3px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: T.mono,
    }}>{cfg.label}</span>
  );
};

// ── Section header helper ─────────────────────────────────────────
const SectionHead = ({ icon: Icon, color, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
    <div style={{
      width: 28, height: 28, borderRadius: 8,
      background: `${color}18`, border: `1px solid ${color}40`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon size={14} color={color} />
    </div>
    <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{label}</span>
  </div>
);

// ── Shared table cell styles ───────────────────────────────────────
const TH = {
  padding: '13px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700,
  letterSpacing: '0.12em', color: 'var(--nd-tx35)', textTransform: 'uppercase',
  fontFamily: "'DM Mono',monospace", whiteSpace: 'nowrap',
  borderBottom: 'var(--nd-w08) 1px solid', background: 'var(--nd-w02)',
};
const TD = { padding: '14px 16px', borderBottom: '1px solid var(--nd-w04)', verticalAlign: 'middle' };

// ── Modal overlay wrapper ─────────────────────────────────────────
const Overlay = ({ onClick, children, zIndex = 9999 }) => (
  <div
    onClick={onClick}
    style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex, padding: 20,
    }}
  >{children}</div>
);

// ── Main Component ────────────────────────────────────────────────
const SitesManagement = () => {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedClientName, setSelectedClientName] = useState('');
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [show2DModal, setShow2DModal] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [editingSiteId, setEditingSiteId] = useState(null);
  const [currentSite, setCurrentSite] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageRotation, setImageRotation] = useState(0);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const blankForm = {
    site_name: '', address: '', city: '', state: '', zip_code: '',
    system_size: '', total_panel_count: '', system_type: 'Commercial',
    installation_status: 'pending', installation_date: '',
    notes: '', latitude: '', longitude: '', images: []
  };
  const [formData, setFormData] = useState(blankForm);

  useEffect(() => { loadClients(); }, []);
  useEffect(() => { if (selectedClientId) loadSites(); else setSites([]); }, [selectedClientId]);

  const loadClients = async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/commercial-clients`);
      if (r.data.success) setClients(r.data.data.clients);
    } catch (e) { console.error('Error loading clients:', e); alert('Failed to load clients'); }
  };

  const loadSites = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/commercial-sites/${selectedClientId}`);
      const raw = Array.isArray(r.data) ? r.data : [];
      setSites(raw.map(s => ({
        ...s,
        images: typeof s.images === 'string' ? (s.images ? JSON.parse(s.images) : []) : (Array.isArray(s.images) ? s.images : [])
      })));
    } catch (e) { console.error('Error loading sites:', e); setSites([]); }
    finally { setLoading(false); }
  };

  const handleClientChange = (e) => {
    const id = e.target.value;
    setSelectedClientId(id);
    const c = clients.find(c => c.id == id);
    setSelectedClientName(c ? c.client_name : '');
    setSites([]); setSearchTerm(''); setFilterStatus('all');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let updated = { ...formData, [name]: value };
    if (name === 'system_size') {
      const kw = parseFloat(value);
      updated.total_panel_count = value && !isNaN(kw) ? Math.ceil((kw * 1000) / 585) : '';
    }
    setFormData(updated);
  };

  const openModal = () => { setEditingSiteId(null); setFormData(blankForm); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditingSiteId(null); };
  const open2DModal = (site) => { setCurrentSite(site); setShow2DModal(true); };
  const close2DModal = () => { setShow2DModal(false); setCurrentSite(null); };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const r = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/commercial-sites/${currentSite.id}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data.site) {
        const updated = { ...r.data.site, images: typeof r.data.site.images === 'string' ? JSON.parse(r.data.site.images) : r.data.site.images };
        setCurrentSite(updated);
        setSites(prev => prev.map(s => s.id === currentSite.id ? updated : s));
        alert(`${files.length} image(s) uploaded successfully!`);
      }
    } catch (e) { console.error('❌ Error uploading images:', e); alert('Failed to upload images: ' + (e.response?.data?.error || e.message)); }
  };

  const removeImage = async (imageId) => {
    if (!currentSite || !window.confirm('Are you sure you want to delete this image?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/commercial-sites/${currentSite.id}/image`, { data: { imageId } });
      const updatedImages = currentSite.images.filter(img => img.id !== imageId);
      const updatedSite = { ...currentSite, images: updatedImages };
      setCurrentSite(updatedSite);
      setSites(prev => prev.map(s => s.id === currentSite.id ? updatedSite : s));
      alert('Image deleted successfully!');
    } catch (e) { console.error('❌ Error deleting image:', e); alert('Failed to delete image: ' + (e.response?.data?.error || e.message)); }
  };

  const save2DImages = () => { alert('Images are saved automatically when uploaded!'); close2DModal(); loadSites(); };

  const openImageViewer = (index) => { setCurrentImageIndex(index); setImageZoom(1); setImageRotation(0); setImagePosition({ x: 0, y: 0 }); setShowImageViewer(true); };
  const closeImageViewer = () => { setShowImageViewer(false); setImageZoom(1); setImageRotation(0); setImagePosition({ x: 0, y: 0 }); };
  const resetImg = () => { setCurrentImageIndex(i => (i + 1) % currentSite.images.length); setImageZoom(1); setImageRotation(0); setImagePosition({ x: 0, y: 0 }); };
  const nextImage = () => { setCurrentImageIndex(i => (i + 1) % currentSite.images.length); setImageZoom(1); setImageRotation(0); setImagePosition({ x: 0, y: 0 }); };
  const prevImage = () => { setCurrentImageIndex(i => (i - 1 + currentSite.images.length) % currentSite.images.length); setImageZoom(1); setImageRotation(0); setImagePosition({ x: 0, y: 0 }); };
  const zoomIn  = () => setImageZoom(p => Math.min(p + 0.25, 4));
  const zoomOut = () => setImageZoom(p => Math.max(p - 0.25, 0.5));
  const rotate  = () => setImageRotation(p => (p + 90) % 360);
  const resetView = () => { setImageZoom(1); setImageRotation(0); setImagePosition({ x: 0, y: 0 }); };

  const handleMouseDown = (e) => { if (imageZoom > 1) { setIsDragging(true); setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y }); } };
  const handleMouseMove = (e) => { if (isDragging) setImagePosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y }); };
  const handleMouseUp   = () => setIsDragging(false);

  const downloadImage = () => {
    if (currentSite?.images[currentImageIndex]) {
      const img = currentSite.images[currentImageIndex];
      const a = document.createElement('a');
      a.href = img.data; a.download = img.name || `site-image-${currentImageIndex + 1}.jpg`; a.click();
    }
  };

  const handleEdit = async (siteId) => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/commercial-sites/${siteId}`);
      const s = r.data;
      setEditingSiteId(siteId);
      setFormData({
        site_name: s.site_name, address: s.address, city: s.city, state: s.state,
        zip_code: s.zip_code || '', system_size: s.system_size || '',
        total_panel_count: s.total_panel_count || '', system_type: s.system_type || 'Commercial',
        installation_status: s.installation_status,
        installation_date: s.installation_date ? s.installation_date.split('T')[0] : '',
        notes: s.notes || '', latitude: s.latitude || '', longitude: s.longitude || '',
        images: s.images || []
      });
      setShowModal(true);
    } catch (e) { console.error('Error loading site:', e); alert('Failed to load site details'); }
  };

  const handleDelete = async (siteId, siteName) => {
    if (!window.confirm(`Are you sure you want to delete "${siteName}"?`)) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/commercial-sites/${siteId}`);
      loadSites();
    } catch (e) { console.error('Error deleting site:', e); alert('Failed to delete site'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, client_id: selectedClientId };
      if (editingSiteId) await axios.put(`${process.env.REACT_APP_API_BASE_URL}/commercial-sites/${editingSiteId}`, formData);
      else await axios.post(`${process.env.REACT_APP_API_BASE_URL}/commercial-sites`, data);
      closeModal(); loadSites();
    } catch (e) { console.error('Error saving site:', e); alert('Failed to save site'); }
  };

  const filteredSites = sites.filter(s => {
    const matchSearch = s.site_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.city.toLowerCase().includes(searchTerm.toLowerCase());
    return matchSearch && (filterStatus === 'all' || s.installation_status === filterStatus);
  });

  // ── Close button shared ──
  const CloseBtn = ({ onClick }) => (
    <button onClick={onClick} style={{
      background: 'var(--nd-w06)', border: `1px solid ${T.border}`,
      color: T.muted, width: 34, height: 34, borderRadius: 9, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
    }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--nd-w12)'}
      onMouseLeave={e => e.currentTarget.style.background = 'var(--nd-w06)'}
    ><X size={15} /></button>
  );

  return (
    <div style={{ background: T.bg, fontFamily: T.font, color: T.text, padding: '14px 20px' }}>
      <style>{STYLES}</style>

      {/* ── PAGE HEADER ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, marginBottom: 18, overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${T.border}` }}>
          <div>
            <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 5 }}>iNOVAA · C&I MANAGEMENT</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.03em', lineHeight: 1 }}>Sites Management</div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 5 }}>Manage installation sites with 2D images</div>
          </div>
          {selectedClientId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--nd-text-strong)', lineHeight: 1, fontFamily: T.mono }}>{sites.length}</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>Total Sites</div>
              </div>
              <button className="sm-btn-primary" onClick={openModal}>
                <Plus size={16} /> Add Site
              </button>
            </div>
          )}
        </div>
        <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />
      </div>

      {/* ── CLIENT SELECTOR ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '16px 20px', marginBottom: 16 }}>
        <label className="sm-label" style={{ marginBottom: 8 }}>Select Client</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <select
            value={selectedClientId}
            onChange={handleClientChange}
            className="sm-select"
            style={{ maxWidth: 380 }}
          >
            <option value="">— Select a Client —</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
          </select>
          {selectedClientName && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.muted }}>
              <Building2 size={13} />
              Managing sites for
              <span style={{ color: T.text, fontWeight: 700 }}>{selectedClientName}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── SEARCH + FILTER ── */}
      {selectedClientId && (
        <div style={{
          background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
          padding: '14px 18px', marginBottom: 16,
          display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
        }}>
          <div className="sm-input-icon" style={{ flex: 1, minWidth: 220 }}>
            <Search size={15} color="var(--nd-tx25)" />
            <input type="text" className="sm-input" placeholder="Search by name, address, or city…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Filter size={14} color={T.muted} />
            <select className="sm-select" style={{ width: 160 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="on_hold">On Hold</option>
            </select>
          </div>
          <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono, marginLeft: 'auto' }}>
            {filteredSites.length} / {sites.length} sites
          </div>
        </div>
      )}

      {/* ── SITES TABLE ── */}
      {selectedClientId && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{ width: 40, height: 40, border: `3px solid ${T.border}`, borderTop: `3px solid ${T.blue}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
              <p style={{ marginTop: 14, color: T.muted, fontSize: 13 }}>Loading sites…</p>
            </div>
          ) : filteredSites.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 14 }}>📍</div>
              <p style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 6 }}>No sites found</p>
              <p style={{ color: T.muted, fontSize: 13, marginBottom: 16 }}>
                {searchTerm ? 'Try adjusting your search criteria' : 'Add your first installation site to get started'}
              </p>
              {!searchTerm && (
                <button className="sm-btn-primary" onClick={openModal} style={{ margin: '0 auto' }}>
                  <Plus size={15} /> Add First Site
                </button>
              )}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...TH, width: 40 }}>#</th>
                    <th style={TH}>Site Name</th>
                    <th style={TH}>Location</th>
                    <th style={TH}>System</th>
                    <th style={TH}>Total Panels</th>
                    <th style={TH}>Status</th>
                    <th style={TH}>2D Images</th>
                    <th style={{ ...TH, textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSites.map((site, idx) => {
                    let panelCount = site.total_panel_count;
                    if (!panelCount && site.system_size) {
                      const kw = parseFloat(site.system_size);
                      if (!isNaN(kw)) panelCount = Math.ceil((kw * 1000) / 585);
                    }
                    const hasImages = site.images && site.images.length > 0;

                    return (
                      <tr key={site.id} className="sm-row">
                        <td style={TD}><span style={{ color: T.muted, fontSize: 11, fontFamily: T.mono }}>{idx + 1}</span></td>

                        {/* Site Name */}
                        <td style={TD}>
                          <div style={{ fontWeight: 700, color: T.text, fontSize: 14, marginBottom: 3 }}>{site.site_name}</div>
                          {site.system_type && <div style={{ fontSize: 11, color: T.muted }}>{site.system_type}</div>}
                        </td>

                        {/* Location */}
                        <td style={TD}>
                          <div style={{ fontSize: 13, color: T.text, marginBottom: 3 }}>{site.address}</div>
                          <div style={{ fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <MapPin size={10} /> {site.city}, {site.state} {site.zip_code}
                          </div>
                        </td>

                        {/* System */}
                        <td style={TD}>
                          {site.system_size ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <Zap size={13} color={T.amber} />
                              <span style={{ fontWeight: 700, color: T.text, fontSize: 13, fontFamily: T.mono }}>
                                {parseFloat(site.system_size).toLocaleString()}
                              </span>
                              <span style={{ fontSize: 10, color: T.muted }}>kW</span>
                            </div>
                          ) : <span style={{ color: T.muted, fontSize: 12 }}>—</span>}
                        </td>

                        {/* Total Panels */}
                        <td style={TD}>
                          {panelCount ? (
                            <div>
                              <div style={{ fontWeight: 700, color: T.teal, fontSize: 15, fontFamily: T.mono }}>
                                {parseInt(panelCount).toLocaleString()}
                              </div>
                              <div style={{ fontSize: 10, color: T.muted, display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
                                <Zap size={9} /> Total Panels
                              </div>
                            </div>
                          ) : <span style={{ color: T.muted, fontSize: 12 }}>—</span>}
                        </td>

                        {/* Status */}
                        <td style={TD}><StatusBadge status={site.installation_status} /></td>

                        {/* 2D Images */}
                        <td style={TD}>
                          <button
                            onClick={() => open2DModal(site)}
                            style={{
                              padding: '6px 12px', border: 'none', borderRadius: 8, cursor: 'pointer',
                              background: hasImages ? 'rgba(59,130,246,0.12)' : 'var(--nd-w05)',
                              color: hasImages ? '#60A5FA' : T.muted,
                              border: `1px solid ${hasImages ? 'rgba(59,130,246,0.25)' : T.border}`,
                              display: 'flex', alignItems: 'center', gap: 6,
                              fontSize: 12, fontWeight: 600, fontFamily: T.font,
                              transition: 'all 0.15s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = hasImages ? 'rgba(59,130,246,0.22)' : 'var(--nd-w08)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = hasImages ? 'rgba(59,130,246,0.12)' : 'var(--nd-w05)'; }}
                          >
                            <ImageIcon size={13} />
                            {hasImages ? `${site.images.length} Images` : 'Upload 2D'}
                          </button>
                        </td>

                        {/* Actions */}
                        <td style={{ ...TD, textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button
                              className="sm-action-btn"
                              onClick={() => handleEdit(site.id)}
                              title="Edit Site"
                              style={{ background: 'rgba(251,191,36,0.10)', border: '1px solid rgba(251,191,36,0.22)', color: T.amber }}
                              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(251,191,36,0.22)'; e.currentTarget.style.transform = 'scale(1.08)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(251,191,36,0.10)'; e.currentTarget.style.transform = 'scale(1)'; }}
                            ><Edit size={14} /></button>
                            <button
                              className="sm-action-btn"
                              onClick={() => handleDelete(site.id, site.site_name)}
                              title="Delete Site"
                              style={{ background: 'rgba(248,113,113,0.10)', border: '1px solid rgba(248,113,113,0.22)', color: T.red }}
                              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(248,113,113,0.22)'; e.currentTarget.style.transform = 'scale(1.08)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(248,113,113,0.10)'; e.currentTarget.style.transform = 'scale(1)'; }}
                            ><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════
          ADD / EDIT SITE MODAL
      ══════════════════════════════════════════════ */}
      {showModal && (
        <Overlay onClick={closeModal}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: T.surface, border: `1px solid ${T.bord2}`, borderRadius: 20,
              maxWidth: 740, width: '100%', maxHeight: '90vh', overflow: 'hidden',
              boxShadow: '0 24px 80px rgba(0,0,0,0.6)', animation: 'modalIn 0.2s ease both',
              display: 'flex', flexDirection: 'column',
            }}
          >
            {/* Modal Header */}
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--nd-w02)' }}>
              <div>
                <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 4 }}>SITES MANAGEMENT</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.02em' }}>
                  {editingSiteId ? 'Edit Site' : 'Add New Site'}
                </div>
              </div>
              <CloseBtn onClick={closeModal} />
            </div>
            <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)', flexShrink: 0 }} />

            {/* Modal Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
              <form onSubmit={handleSubmit} id="siteModalForm">

                {/* Site Name */}
                <div style={{ marginBottom: 16 }}>
                  <SectionHead icon={Building2} color={T.blue} label="Site Details" />
                  <label className="sm-label">Site Name <span style={{ color: T.red }}>*</span></label>
                  <input type="text" name="site_name" value={formData.site_name} onChange={handleInputChange} required placeholder="e.g., Main Office Building" className="sm-input" />
                </div>

                {/* Address */}
                <div style={{ marginBottom: 16 }}>
                  <label className="sm-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <MapPin size={10} /> Address <span style={{ color: T.red }}>*</span>
                  </label>
                  <input type="text" name="address" value={formData.address} onChange={handleInputChange} required placeholder="Street address" className="sm-input" />
                </div>

                {/* City / State */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
                  <div>
                    <label className="sm-label">City <span style={{ color: T.red }}>*</span></label>
                    <input type="text" name="city" value={formData.city} onChange={handleInputChange} required className="sm-input" />
                  </div>
                  <div>
                    <label className="sm-label">State <span style={{ color: T.red }}>*</span></label>
                    <input type="text" name="state" value={formData.state} onChange={handleInputChange} required className="sm-input" />
                  </div>
                </div>

                {/* ZIP / System Size / Panel Count */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 16 }}>
                  <div>
                    <label className="sm-label">ZIP Code</label>
                    <input type="text" name="zip_code" value={formData.zip_code} onChange={handleInputChange} className="sm-input" />
                  </div>
                  <div>
                    <label className="sm-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Zap size={9} color={T.amber} /> System Size (kW)
                    </label>
                    <input type="number" step="0.01" name="system_size" value={formData.system_size} onChange={handleInputChange} className="sm-input" />
                  </div>
                  <div>
                    <label className="sm-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Zap size={9} color={T.teal} /> Total Panels
                    </label>
                    <input type="number" step="1" name="total_panel_count" value={formData.total_panel_count} onChange={handleInputChange} className="sm-input" />
                  </div>
                </div>

                {/* System Type / Status */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
                  <div>
                    <label className="sm-label">System Type</label>
                    <select name="system_type" value={formData.system_type} onChange={handleInputChange} className="sm-select">
                      <option value="Residential">Residential</option>
                      <option value="Commercial">Commercial</option>
                      <option value="Industrial">Industrial</option>
                    </select>
                  </div>
                  <div>
                    <label className="sm-label">Installation Status</label>
                    <select name="installation_status" value={formData.installation_status} onChange={handleInputChange} className="sm-select">
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="on_hold">On Hold</option>
                    </select>
                  </div>
                </div>

                {/* Installation Date */}
                <div style={{ marginBottom: 16 }}>
                  <label className="sm-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Calendar size={9} /> Installation Date
                  </label>
                  <input type="date" name="installation_date" value={formData.installation_date} onChange={handleInputChange} className="sm-input" style={{ maxWidth: 240 }} />
                </div>

                {/* GPS Coordinates */}
                <div style={{ background: 'var(--nd-w03)', border: `1px solid ${T.border}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
                    <MapPin size={12} color={T.blue} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.muted, letterSpacing: '0.05em' }}>GPS Coordinates (Optional)</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div>
                      <label className="sm-label">Latitude</label>
                      <input type="number" step="0.00000001" name="latitude" value={formData.latitude} onChange={handleInputChange} placeholder="e.g., 40.7128" className="sm-input" />
                    </div>
                    <div>
                      <label className="sm-label">Longitude</label>
                      <input type="number" step="0.00000001" name="longitude" value={formData.longitude} onChange={handleInputChange} placeholder="e.g., -74.0060" className="sm-input" />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="sm-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Info size={9} /> Notes
                  </label>
                  <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={3} placeholder="Any additional notes about this site…" className="sm-textarea" />
                </div>
              </form>
            </div>

            {/* Modal Footer */}
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${T.border}`, display: 'flex', gap: 10, background: 'var(--nd-w02)' }}>
              <button type="button" className="sm-btn-ghost" onClick={closeModal} style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
              <button type="submit" form="siteModalForm" className="sm-btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
                {editingSiteId ? '✓ Update Site' : '+ Create Site'}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ══════════════════════════════════════════════
          2D IMAGES MODAL
      ══════════════════════════════════════════════ */}
      {show2DModal && currentSite && (
        <Overlay onClick={close2DModal} zIndex={10000}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: T.surface, border: `1px solid ${T.bord2}`, borderRadius: 20,
              maxWidth: 1080, width: '100%', maxHeight: '90vh', overflow: 'hidden',
              boxShadow: '0 24px 80px rgba(0,0,0,0.7)', animation: 'modalIn 0.2s ease both',
              display: 'flex', flexDirection: 'column',
            }}
          >
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--nd-w02)' }}>
              <div>
                <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 4 }}>2D IMAGES</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.02em' }}>{currentSite.site_name}</div>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 3 }}>Upload and manage site images, floor plans, and diagrams</div>
              </div>
              <CloseBtn onClick={close2DModal} />
            </div>
            <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)', flexShrink: 0 }} />

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>

              {/* Upload zone */}
              <div style={{
                border: '2px dashed rgba(59,130,246,0.35)', borderRadius: 14,
                padding: '36px 20px', marginBottom: 24, textAlign: 'center',
                background: 'rgba(59,130,246,0.04)', transition: 'border-color 0.15s',
              }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(59,130,246,0.6)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(59,130,246,0.35)'}
              >
                <input type="file" accept="image/*" multiple onChange={handleImageUpload} id="imageUpload" style={{ display: 'none' }} />
                <label htmlFor="imageUpload" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(59,130,246,0.14)', border: '1px solid rgba(59,130,246,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Upload size={26} color={T.blue} />
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 4 }}>Click to Upload Images</div>
                    <div style={{ fontSize: 12, color: T.muted }}>PNG, JPG, JPEG · Multiple files supported</div>
                  </div>
                </label>
              </div>

              {/* Images grid */}
              {currentSite.images && currentSite.images.length > 0 ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                    <ImageIcon size={15} color={T.blue} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
                      Uploaded Images
                    </span>
                    <span style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>({currentSite.images.length})</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(190px,1fr))', gap: 16 }}>
                    {currentSite.images.map((image, index) => (
                      <div key={image.id} className="sm-img-card" style={{
                        borderRadius: 12, overflow: 'hidden',
                        background: T.surf2, border: `1px solid ${T.border}`,
                        cursor: 'pointer', boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                      }}>
                        {/* Image preview */}
                        <div
                          onClick={() => openImageViewer(index)}
                          style={{ position: 'relative', width: '100%', height: 180, overflow: 'hidden', background: 'var(--nd-bg)' }}
                        >
                          <img src={image.data} alt={image.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          <div style={{
                            position: 'absolute', inset: 0,
                            background: 'linear-gradient(to bottom,rgba(0,0,0,0),rgba(0,0,0,0.55))',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            opacity: 0, transition: 'opacity 0.2s',
                          }}
                            onMouseEnter={e => e.currentTarget.style.opacity = 1}
                            onMouseLeave={e => e.currentTarget.style.opacity = 0}
                          >
                            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--nd-w15)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Eye size={20} color="white" />
                            </div>
                          </div>
                        </div>
                        {/* Card footer */}
                        <div style={{ padding: '10px 12px' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {image.name}
                          </div>
                          <div style={{ fontSize: 10, color: T.muted, marginBottom: 8, fontFamily: T.mono }}>
                            {new Date(image.uploadDate).toLocaleDateString()}
                          </div>
                          <button
                            onClick={e => { e.stopPropagation(); removeImage(image.id); }}
                            style={{
                              width: '100%', padding: '6px 0', border: '1px solid rgba(248,113,113,0.25)',
                              background: 'rgba(248,113,113,0.08)', color: T.red,
                              borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                              fontFamily: T.font, transition: 'all 0.15s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(248,113,113,0.2)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(248,113,113,0.08)'; }}
                          >
                            <Trash2 size={12} /> Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '50px 20px', border: `2px dashed ${T.border}`, borderRadius: 12, background: 'var(--nd-w02)' }}>
                  <ImageIcon size={48} color={T.faint} style={{ marginBottom: 12 }} />
                  <p style={{ fontSize: 14, fontWeight: 600, color: T.muted, margin: 0 }}>No images uploaded yet</p>
                  <p style={{ fontSize: 12, color: 'var(--nd-w20)', marginTop: 6 }}>Upload your first 2D image above</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--nd-w02)' }}>
              <div style={{ fontSize: 12, color: T.muted, fontFamily: T.mono }}>
                {currentSite.images?.length > 0
                  ? <><span style={{ color: T.blue, fontWeight: 700 }}>{currentSite.images.length}</span> image{currentSite.images.length !== 1 ? 's' : ''} uploaded</>
                  : 'No images uploaded'}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="sm-btn-ghost" onClick={close2DModal}>Close</button>
                <button className="sm-btn-primary" onClick={save2DImages}>✓ Save Changes</button>
              </div>
            </div>
          </div>
        </Overlay>
      )}

      {/* ══════════════════════════════════════════════
          FULL-SCREEN IMAGE VIEWER
      ══════════════════════════════════════════════ */}
      {showImageViewer && currentSite?.images[currentImageIndex] && (
        <div
          onClick={closeImageViewer}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.97)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001, padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 1380, height: '95vh', display: 'flex', flexDirection: 'column', background: 'var(--nd-bg)', borderRadius: 20, overflow: 'hidden', border: `1px solid ${T.bord2}`, boxShadow: '0 30px 80px rgba(0,0,0,0.8)' }}
          >
            {/* Viewer Header */}
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--nd-w02)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <ImageIcon size={16} color={T.blue} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{currentSite.images[currentImageIndex].name}</div>
                  <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>
                    {currentImageIndex + 1} / {currentSite.images.length}
                  </div>
                </div>
              </div>
              <CloseBtn onClick={closeImageViewer} />
            </div>

            {/* Image canvas */}
            <div
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: 'var(--nd-bg)', position: 'relative', cursor: imageZoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
              onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
            >
              <img
                src={currentSite.images[currentImageIndex].data}
                alt={currentSite.images[currentImageIndex].name}
                style={{
                  maxWidth: imageZoom === 1 ? '100%' : 'none',
                  maxHeight: imageZoom === 1 ? '100%' : 'none',
                  width: imageZoom > 1 ? `${imageZoom * 100}%` : 'auto',
                  height: imageZoom > 1 ? `${imageZoom * 100}%` : 'auto',
                  objectFit: 'contain',
                  transform: `translate(${imagePosition.x}px,${imagePosition.y}px) rotate(${imageRotation}deg)`,
                  transition: isDragging ? 'none' : 'transform 0.3s ease',
                  userSelect: 'none', pointerEvents: 'none',
                }}
                draggable="false"
              />

              {/* Nav arrows */}
              {currentSite.images.length > 1 && (
                <>
                  {[{ pos: 'left', fn: prevImage, label: '‹' }, { pos: 'right', fn: nextImage, label: '›' }].map(({ pos, fn, label }) => (
                    <button key={pos} onClick={fn} style={{
                      position: 'absolute', [pos]: 20, top: '50%', transform: 'translateY(-50%)',
                      width: 50, height: 50, borderRadius: '50%', border: 'none', cursor: 'pointer',
                      background: 'var(--nd-w10)', backdropFilter: 'blur(8px)',
                      color: 'var(--nd-text-strong)', fontSize: 26, fontWeight: 700, zIndex: 10,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.15s',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--nd-tx22)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.08)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'var(--nd-w10)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; }}
                    >{label}</button>
                  ))}
                </>
              )}

              {/* Zoom indicator */}
              {imageZoom > 1 && (
                <div style={{
                  position: 'absolute', top: 14, left: 14,
                  background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
                  color: T.text, padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 5, fontFamily: T.mono,
                }}>
                  <Move size={13} /> Drag to pan
                </div>
              )}
            </div>

            {/* Control bar */}
            <div style={{ padding: '14px 20px', borderTop: `1px solid ${T.border}`, background: 'var(--nd-w02)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, flexShrink: 0 }}>
              {/* Zoom controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--nd-w06)', padding: '8px 14px', borderRadius: 10, border: `1px solid ${T.border}` }}>
                <button onClick={zoomOut} disabled={imageZoom <= 0.5} style={{ background: imageZoom <= 0.5 ? 'transparent' : 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 7, padding: 7, cursor: imageZoom <= 0.5 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', opacity: imageZoom <= 0.5 ? 0.4 : 1, transition: 'all 0.15s' }}>
                  <ZoomOut size={16} color={T.blue} />
                </button>
                <span style={{ color: T.text, fontSize: 13, fontWeight: 700, minWidth: 52, textAlign: 'center', fontFamily: T.mono }}>{Math.round(imageZoom * 100)}%</span>
                <button onClick={zoomIn} disabled={imageZoom >= 4} style={{ background: imageZoom >= 4 ? 'transparent' : 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 7, padding: 7, cursor: imageZoom >= 4 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', opacity: imageZoom >= 4 ? 0.4 : 1, transition: 'all 0.15s' }}>
                  <ZoomIn size={16} color={T.blue} />
                </button>
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { label: 'Rotate', icon: RotateCw, color: T.teal, bg: 'rgba(34,211,165,0.12)', border: 'rgba(34,211,165,0.25)', fn: rotate },
                  { label: 'Reset',  icon: Maximize2, color: T.purple, bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.25)', fn: resetView },
                  { label: 'Download', icon: Download, color: T.blue, bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.25)', fn: downloadImage },
                ].map(({ label, icon: Icon, color, bg, border, fn }) => (
                  <button key={label} onClick={fn} style={{
                    padding: '8px 16px', border: `1px solid ${border}`, borderRadius: 9,
                    background: bg, color, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6,
                    fontSize: 12, fontWeight: 700, fontFamily: T.font, transition: 'all 0.15s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.filter = 'brightness(1.2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.filter = 'brightness(1)'; }}
                  ><Icon size={14} /> {label}</button>
                ))}
              </div>
            </div>

            {/* Thumbnail strip */}
            {currentSite.images.length > 1 && (
              <div style={{ padding: '12px 20px', borderTop: `1px solid ${T.border}`, background: 'var(--nd-bg)', overflowX: 'auto', display: 'flex', gap: 10, flexShrink: 0 }}>
                {currentSite.images.map((image, idx) => (
                  <div
                    key={image.id}
                    onClick={() => { setCurrentImageIndex(idx); setImageZoom(1); setImageRotation(0); setImagePosition({ x: 0, y: 0 }); }}
                    style={{
                      minWidth: 88, height: 66, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', flexShrink: 0,
                      border: `2px solid ${idx === currentImageIndex ? T.blue : 'transparent'}`,
                      opacity: idx === currentImageIndex ? 1 : 0.5, transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { if (idx !== currentImageIndex) e.currentTarget.style.opacity = 0.8; }}
                    onMouseLeave={e => { if (idx !== currentImageIndex) e.currentTarget.style.opacity = 0.5; }}
                  >
                    <img src={image.data} alt={image.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SitesManagement;